/**
 * SignInRequirementBanner — banner-center source (docs/PLAN-signin-requirements-20261029.md, "The screens" 4; the
 * contract: "announced before enforced", "counted down").
 *
 * A person whose workspace requires two-step sign-in by a date, and who has not set it up, is counted down to that date
 * in the band: calm and dismissible for the day while more than three days remain, not dismissible from then, critical
 * on the day (signInRequirementBannerPolicy.ts). Its one action, "set it up", leads to Settings' two-step card. The rule
 * is the policy's and the words signInRequirementCopy.ts; this is the wiring:
 *
 *   - who: the signed-in person (not anonymous), in the tab's workspace (resolveTabWorkspace, as Settings resolves it),
 *     where the client lever admits it (REACT_APP_SIGNIN_REQUIREMENTS);
 *   - what: the person's own status, read once per page through the band's shared read (useSignInSecurityStatus: its
 *     client loads on demand, in its own chunk, since the band is on the first download), read again on leaving
 *     Settings and when the tab is shown again;
 *   - dismissed: the X holds the calm notice until 5:00 AM local the next morning (bannerDismissal's daily rule).
 *
 * The platform's email requirement is the email notice's own (EmailVerifyBanner.tsx): one notice per requirement.
 */
import React, { useContext, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useAuth } from '../../context/AuthContext';
import { useBannerSurface } from './bannerSurface';
import { BannerBusContext, useRegisterBanner, type BannerDescriptor } from './GlobalBannerCenter';
import type { BannerNotice } from './bannerNotice';
import { isDismissalActive, readDailyDismissal, writeDailyDismissal } from './bannerDismissal';
import { signInRequirementsShow } from '../../services/identity/signInRequirementsLever';
import { resolveTabWorkspace } from '../../services/prest0nRuntime/tabWorkspace';
import { appPathname } from '../../design/previewPath';
import { isSettingsPath } from './provePhoneBannerPolicy';
import { useSignInSecurityStatus } from '../../hooks/useSignInSecurityStatus';
import { SET_UP_TWO_STEP_TO } from '../../lib/signInSecurity/signInRequirementAddress';
import { SIGN_IN_REQUIREMENT_COPY } from './signInRequirementCopy';
import {
  COUNTDOWN_KIND,
  countdownDismissible,
  countdownShows,
  countdownStage,
  countdownTone,
  pressingRequirement,
} from './signInRequirementBannerPolicy';

const WORDS = SIGN_IN_REQUIREMENT_COPY.twoStep;

/** The calm notice's daily dismissal (5:00 AM local the next morning). */
export const SIGN_IN_REQUIREMENT_DISMISS_KEY = 'prest0n_signinreq_dismissed';

const ActionLink = styled(Link)`
  flex: none;
  padding: 4px 14px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  color: #fff;
  background: #8a6b2a;
  text-decoration: none;
  transition: filter 0.15s ease;
  &:hover { filter: brightness(1.08); }
`;

export const SignInRequirementBanner: React.FC = () => {
  const bus = useContext(BannerBusContext);
  const onAppSurface = useBannerSurface();
  const { pathname } = useLocation();
  const onSettings = isSettingsPath(appPathname(pathname));
  const { user } = useAuth();

  const [uid, setUid] = useState<string | null>(null);
  useEffect(() => onAuthStateChanged(getAuth(), (u) => setUid(u && !u.isAnonymous ? u.uid : null)), []);

  const accountId = uid ? resolveTabWorkspace({ uid, accountFromPointer: user?.accountId }).accountId : null;
  const leverAdmits = !!accountId && signInRequirementsShow(accountId);
  const status = useSignInSecurityStatus(uid, leverAdmits, onSettings);

  const [dismissedAt, setDismissedAt] = useState<string | null>(() => readDailyDismissal(SIGN_IN_REQUIREMENT_DISMISS_KEY));
  const requirement = pressingRequirement(status, COUNTDOWN_KIND);
  const stage = requirement ? countdownStage(requirement, Date.now()) : null;
  const shows = countdownShows({
    signedIn: !!uid,
    leverAdmits,
    onAppSurface,
    requirement,
    stage,
    dismissedToday: isDismissalActive(dismissedAt),
  });

  const handleDismiss = () => {
    const now = new Date();
    writeDailyDismissal(SIGN_IN_REQUIREMENT_DISMISS_KEY, now);
    setDismissedAt(now.toISOString());
  };

  let descriptor: BannerDescriptor | null = null;
  if (shows && requirement && stage) {
    const words = WORDS.words(stage, requirement.workspaces, requirement.dueAtMs);
    const shortWords = WORDS.wordsShort(stage, requirement.dueAtMs);
    const dismissible = countdownDismissible(stage);
    const notice: BannerNotice = { words, shortWords, action: { label: WORDS.action, to: SET_UP_TWO_STEP_TO } };
    descriptor = {
      id: 'signin-requirement',
      tone: countdownTone(stage, 'info'),
      dismissible,
      onDismiss: dismissible ? handleDismiss : undefined,
      notice,
      shortContent: (
        <>
          <span>{shortWords}</span>
          <ActionLink to={SET_UP_TWO_STEP_TO}>{WORDS.action}</ActionLink>
        </>
      ),
      content: (
        <>
          <span>{words}</span>
          <ActionLink to={SET_UP_TWO_STEP_TO}>{WORDS.action}</ActionLink>
        </>
      ),
    };
  }

  useRegisterBanner(bus ? descriptor : null);

  // Banner-center-native: outside the center this source renders nothing.
  return null;
};

export default SignInRequirementBanner;
